import { useEffect, useState } from 'react';
import { Button } from '@/client/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/client/components/ui/dialog';
import { ProjectFormFields, emptyProjectForm } from './ProjectFormFields';
import type { ProjectFormState } from './ProjectFormFields';

type ProjectForDialog = {
  id: string;
  name: string;
  description: string | null;
  status: 'active' | 'on_hold' | 'completed';
  client_name: string | null;
};

interface ProjectDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  project: ProjectForDialog | null;
  onSubmit: (form: ProjectFormState) => void;
  isPending: boolean;
  error: string | null;
}

export function ProjectDialog({
  open,
  onOpenChange,
  project,
  onSubmit,
  isPending,
  error,
}: ProjectDialogProps) {
  const [form, setForm] = useState<ProjectFormState>(emptyProjectForm);

  useEffect(() => {
    if (open) {
      setForm(
        project
          ? {
              name: project.name,
              description: project.description ?? '',
              status: project.status,
              client_name: project.client_name ?? '',
            }
          : emptyProjectForm,
      );
    }
  }, [open, project]);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    onSubmit(form);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{project === null ? 'Add Project' : 'Edit Project'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <ProjectFormFields form={form} setForm={setForm} />
          {error && <p className="text-sm text-destructive">{error}</p>}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={isPending}>
              {isPending ? 'Saving…' : project === null ? 'Create' : 'Save'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
